import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { LogoMark } from "./primitives";

const Navbar = () => {
  const config = useSiteConfig();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) return;
    const ids = config.nav.map((n) => n.href.slice(1));
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome, config.nav]);

  const go = (href) => {
    setOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => window.dispatchEvent(new CustomEvent("jcv:scroll", { detail: href })), 120);
    } else {
      window.dispatchEvent(new CustomEvent("jcv:scroll", { detail: href }));
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-nav" data-testid="navbar">
      <nav className="section-shell flex items-center justify-between h-16 lg:h-20" aria-label="Primary">
        <button onClick={() => go("#home")} className="shrink-0" data-testid="nav-logo" aria-label="Jovino Coin home">
          <LogoMark />
        </button>

        <ul className="hidden xl:flex items-center gap-7">
          {config.nav.map((item) => (
            <li key={item.href}>
              <button
                onClick={() => go(item.href)}
                data-testid={`nav-link-${item.href.slice(1)}`}
                className={`font-mono text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                  active === item.href.slice(1) ? "text-teal" : "text-slate hover:text-mist"
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("#presale")}
            data-testid="nav-presale-cta"
            className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gold/50 px-5 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase text-gold transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Presale — 1 Nov 2026
          </button>
          <button
            className="xl:hidden text-mist p-2"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-line bg-navy/95 backdrop-blur-xl"
            data-testid="nav-mobile-drawer"
          >
            <ul className="section-shell py-6 space-y-1">
              {config.nav.map((item, i) => (
                <motion.li
                  key={item.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.3 }}
                >
                  <button
                    onClick={() => go(item.href)}
                    data-testid={`nav-mobile-link-${item.href.slice(1)}`}
                    className="flex w-full items-center justify-between py-3 font-display text-lg text-mist hover:text-teal transition-colors duration-300"
                  >
                    {item.label}
                    <span className="font-mono text-[10px] text-slate">{String(i + 1).padStart(2, "0")}</span>
                  </button>
                </motion.li>
              ))}
              <li className="pt-4">
                <button
                  onClick={() => go("#presale")}
                  data-testid="nav-mobile-presale-cta"
                  className="w-full rounded-full border border-gold/50 px-5 py-3 font-mono text-xs tracking-[0.15em] uppercase text-gold"
                >
                  Presale — 1 Nov 2026
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
